import { CourseSetDTO, CustomerDTO, ScheduledGradeDTO, SiteDTO } from '@beable/roster-backend-client-v1';
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import courseService, { DeliveryRules } from '../../../services/CourseService';
import SchoolSelector from '../../Common/Selectors/SchoolSelector';
import { useDeliveryStrings } from '../../Courses/useDeliveryStrings';
import ScheduleTable from '../ScheduleTable';
import { getStyles } from '../styles';
import AccessTree from './AccessTree';

interface CourseAccordionProps {
    customer: CustomerDTO;
    site: SiteDTO;
    academicYearId: string;
}

export default function CourseAccordion({ customer, site, academicYearId }: CourseAccordionProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { getFrequencyString, getAvailabilityString, getQuantityString } = useDeliveryStrings();

  const [provisionedCourseSets, setProvisionedCourseSets] = useState<CourseSetDTO[]>([]);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [selectedCourseSet, setSelectedCourseSet] = useState<CourseSetDTO | undefined>();

  const [orgId, setOrgId] = useState<string>();

  const [deliveryRules, setDeliveryRules] = useState<DeliveryRules>();
  const [accessData, setAccessData] = useState<ScheduledGradeDTO[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);

  useEffect(() => {
      const fetchCourseSets = async () => {
        const data = await courseService.getProvisionedContentCourses(site.id, orgId!, academicYearId);
        if (data) {
          data.courseSets.forEach((courseSet, index) => {
            data.courseSets[index].courses = courseSet.courses.sort((a,b) => (a.sort - b.sort));
          });
          setProvisionedCourseSets(data.courseSets);
        } else {
          setProvisionedCourseSets([]);
        }
        refreshView()
      };

      if (orgId) {
        fetchCourseSets();
        setIsLoading(false);
      }
  }, [site, orgId, academicYearId]);

  const refreshView = () => {
    setAccessData([]);
    setScheduleData([]);
    setDeliveryRules(undefined);
    setSelectedCourseSet(undefined);
    setExpanded(false);
  }

  useEffect(() => {
    const fetchDeliveryRules = async () => {
      if (selectedCourseSet) {
        const data = await courseService.getCourseDeliveryRules(site.id, undefined, selectedCourseSet.id, orgId);
        if (data) {
          const deliveryRules = {
            frequencyType: data.frequencyType,
            frequencyPayload: data.frequencyPayload,
            availabilityType: data.availabilityType,
            availabilityPayload: data.availabilityPayload,
          }
          setDeliveryRules(deliveryRules);
        }
      }
    };

    const fetchCourseSetData = async () => {
      if (selectedCourseSet && selectedCourseSet.id) {
        const data = await courseService.getCourseScheduleData(site.id, selectedCourseSet.id, orgId!, academicYearId);
        if (data) {
          setAccessData(data.accessTree);
          const schedule = data.assignments.map(assignment => {
            const lesson = data.lessons.find(lesson => (
              lesson.lessonId === assignment.lessonId
              && lesson.beableCourseId === assignment.courseId
            ))

            return {
              courseId: assignment.courseId,
              courseName: lesson?.beableCourseName,
              lessonId: assignment.lessonId,
              lessonName: lesson?.title,
              unitId: lesson?.unitId,
              unitName: lesson?.unitName,
              lessonSummary: lesson?.summary?.blocks?.[0]?.text,
              startDate: assignment.startDt,
              endDate: assignment.endDt,
            }
          })
          setScheduleData(schedule);
        }
      }
    };

    setIsLoadingAccess(true);
    setAccessData([]);
    setScheduleData([]);
    setDeliveryRules(undefined);

    fetchCourseSetData();
    fetchDeliveryRules();
    setIsLoadingAccess(false);
  }, [selectedCourseSet, site, orgId, academicYearId]);

  const handleChange = (courseSetId: string) => async (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? courseSetId : false);
    setSelectedCourseSet(provisionedCourseSets.find(courseSet => courseSet.id === courseSetId));
  };

  const handleChangeOrg = (orgId: string) => {
    if (orgId) {
      setIsLoading(true);
    }
    setOrgId(orgId);
  }

  const reprovisionClass = async (classId: string) => {
    const result = await courseService.reprovisionClass(
      site.id,
      orgId!,
      classId,
      academicYearId,
    );

    if (result) {
      alert('Provision request submitted successfully');
    } else {
      alert('There was an error submitting your request.');
    }
  }

  const getTitle = (courseSet: CourseSetDTO) => {
    if (courseSet.autoGenerated) {
      return  <>
        <Typography component="span" sx={{ fontWeight:'bold' }}>{courseSet.courses[0].courseName}</Typography> (Course)
      </>
    } else {
      return <>
        <Typography component="span" sx={{ fontWeight:'bold' }}>{courseSet.name}</Typography> (Course Set)
      </>;
    }
  }

  const getCsvData = () => {
    const data = scheduleData.map(row => {
      return {
        createdOn: moment().format('YYYY-MM-DD'),
        customerName: customer.name,
        courseName: row.courseName,
        unitName: row.unitName,
        lessonTitle: row.lessonName,
        lessonSummary: row.lessonSummary,
        startDate: moment(row.startDate).format('YYYY-MM-DD'),
        endDate: moment(row.endDate).format('YYYY-MM-DD'),
      }
    })

    return data;
  }

  const csvHeaders = [
    { label: "Created On", key: 'createdOn' },
    { label: "Customer Name", key: "customerName" },
    { label: "Course Name", key: "courseName" },
    { label: "Unit Name", key: "unitName" },
    { label: "Lesson Title", key: "lessonTitle" },
    { label: "Lesson Summary", key: "lessonSummary" },
    { label: "Lesson Start Date", key: "startDate" },
    { label: "Lesson End Date", key: "endDate" },
    { label: "Standards", key: "" },
  ];

  return (
    <Box sx={styles.schedule_root}>
      <Grid container sx={styles.nudgedSelectlist} size={5}>
        <SchoolSelector
          siteId={site ? site.id : 0}
          onChange={(schoolId: string) => handleChangeOrg(schoolId)}
          variant='outlined'
        />
      </Grid>
      { !isLoading && orgId && (!provisionedCourseSets || provisionedCourseSets.length < 1) ? <Typography>No provisioned content courses found</Typography> : null}
      { isLoading &&  (
          <Box sx={styles.loaderContainer}>
            <CircularProgress disableShrink />
          </Box>
      )}
      { provisionedCourseSets && provisionedCourseSets.map((courseSet) => (
      <Accordion
        key={courseSet.id}
        expanded={expanded === courseSet.id}
        onChange={handleChange(courseSet.id!)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="course-accordion-content"
        >
          <Typography>{getTitle(courseSet)}</Typography>
        </AccordionSummary>
        { expanded && selectedCourseSet && selectedCourseSet === courseSet && (
        <AccordionDetails sx={styles.schedule_details}>
          { !courseSet.autoGenerated &&
          <>
          <Typography sx={styles.schedule_subHeader}>Courses: </Typography>
          <Box sx={styles.schedule_sectionContainer}>
            { courseSet?.courses && courseSet?.courses.map(course => (
              <Typography variant="body2" key={course.courseId}>{course.courseName}</Typography>
            ))}
          </Box>
          </>
          }
          <Typography sx={styles.schedule_subHeader}>Access: </Typography>
          <Box sx={styles.schedule_sectionContainer}>
            { isLoadingAccess && (
              <Box sx={styles.loaderContainer}>
                <CircularProgress disableShrink />
              </Box>)
            }
            { !isLoadingAccess && accessData.length > 0 && (
              <AccessTree
                data={accessData}
                reprovisionClass={reprovisionClass}
              />
            )}
            { !isLoadingAccess && !accessData.length && (
              <Typography variant="body2">
                No provisioned classes found for access
              </Typography>
            )}
          </Box>
          { deliveryRules && (
          <>
          <Typography sx={styles.schedule_subHeader}>Delivery: </Typography>
          <Box sx={styles.schedule_sectionContainer}>
            <Typography variant="body2">
              Availability: {getAvailabilityString(deliveryRules)} <br />
              Frequency: {getFrequencyString(deliveryRules)} <br />
              Lessons to deliver: {getQuantityString(deliveryRules)} <br />
            </Typography>
          </Box>
          </>
          )}
          <Grid
            container
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
            <Typography sx={styles.schedule_subHeader}>Schedule: </Typography>
            <CSVLink
              data={getCsvData()}
              headers={csvHeaders}
              filename={`${customer.name}_${courseSet.autoGenerated ? courseSet.courses[0].courseName : courseSet.name}_${moment().format('YYYY-MM-DD')}.csv`}
            >
              <IconButton aria-label="export" title="Export" size="large">
                <SystemUpdateAltIcon/>
              </IconButton>
            </CSVLink>
          </Grid>
          <Box sx={styles.schedule_sectionContainer}>
            { scheduleData.length > 0 ?
              <ScheduleTable
                data={scheduleData}
                showCourse={true}
                showClasses={false}
                showLessons={true}
              /> : <Typography variant="body2">No schedules created</Typography>
            }
          </Box>
        </AccordionDetails>
        )}
      </Accordion>
      ))}
    </Box>
  );
}
